import { BaseModel } from '../../model/base/BaseModel';
import { BaseFilterModel } from '../../model/filter/BaseFilterModel';
import { Weather2Model } from '../../model/weather/Weather2Model.js';
import { BaseCollection } from '../BaseCollection';
import { SprayWeatherCollection } from '../../collection/weather/SprayWeatherCollection';

/**
 * @class Weather2Collection
 * @extends BaseCollection
 */
export class Weather2Collection extends BaseCollection {

    get model() {

        return Weather2Model;
    }

    initialize(models, options) {

        super.initialize(models, options);

        this.microservice = 'weather/v2';
        this.resource = 'data';
    }

    static get FORMAT() {

        return {
            DAILY: 'DAILY',
            HOURLY: 'HOURLY'
        };
    }

    static get PROVIDER() {

        return {
            AUTO: 'AUTO',
            ITERIS: 'ITERIS'
        };
    }

    static get PARAMS() {

        return {
            UUID_FOREIGN: 'uuidForeign',
            DATE: 'date',
            START_DATETIME: 'startDatetime',
            AIR_TEMP_C_MAX: 'airTempCMax',
            AIR_TEMP_C_MIN: 'airTempCMin',
            AIR_TEMP_C_AVG: 'airTempCAvg',
            AIR_TEMP_C_STD_DEV: 'airTempCStddev',
            BELOW_DEVICE_TEMP_C_AVG: 'belowDeviceTempCAvg',
            CLOUD_COVER_PCT_AVG: 'cloudCoverPctAvg',
            LEAF_WETNESS_DURATION_H: 'leafWetnessDurationH',
            PRECIPITATION_BEST_MM: 'precipitationBestMm',
            PRECIPITATION_MM_ACC: 'precipitationMmAcc',
            PRECIPITATION_PROBABILITY_PCT: 'precipitationProbabilityPct',
            RELATIVE_HUMIDITY_PCT_AVG: 'relativeHumidityPctAvg',
            SUNSHINE_DURATION_H: 'sunshineDurationH',
            WATER_BALANCE_MM: 'waterBalanceMm',
            WIND_DIRECTION_DEG: 'windDirectionDeg',
            WIND_SPEED_MS_AVG: 'windSpeedMSAvg',
            WEATHER_DESCRIPTOR_IMAGE: 'weatherDescriptorImage',
            DELTA_TC_AVG: 'deltaTCAvg',
        };
    }

    static get TYPE() {

        return {
            FORECAST: 'FORECAST',
            HISTORIC: 'HISTORIC',
            CLIMATOLOGY10Y: 'CLIMATOLOGY10Y'
        };
    }

    /**
  * @description This method fetches daily weather data
  *
  * @param {number} [days = 7] - Number of days weather data has to be fetched including today
  *
  * @returns {Object} $.Deferred()
  *
  * @memberOf Weather2Collection
  */
    fetchDailyWeatherPrediction({ foreignUuid, days = 7, startDate, endDate }) {

        let today = BaseModel.getLocalMoment();
        let utcFixOffset = 1;
        let fromDate;
        let tillDate;

        if (BaseModel.timezone === 'UTC') {
            today.add(-1, 'd');
            utcFixOffset = 0;
        }
        if(startDate && endDate) {
            fromDate = startDate;
            tillDate = endDate;
        } else {
            fromDate = BaseModel.serializeDate(today.startOf('day'));
            tillDate = BaseModel.serializeDate(today.add(days - utcFixOffset, 'd').startOf('day'));
        }
        let fetched = $.Deferred();

        let weather = new Weather2Collection([], {
            filterParams: new BaseFilterModel({
                foreignUuid: [foreignUuid],
                $fromDate: fromDate,
                $tillDate: tillDate,
                $params: [
                    Weather2Collection.PARAMS.UUID_FOREIGN,
                    Weather2Collection.PARAMS.DATE,
                    Weather2Collection.PARAMS.START_DATETIME,
                    Weather2Collection.PARAMS.AIR_TEMP_C_MAX,
                    Weather2Collection.PARAMS.AIR_TEMP_C_MIN,
                    Weather2Collection.PARAMS.RELATIVE_HUMIDITY_PCT_AVG,
                    Weather2Collection.PARAMS.PRECIPITATION_BEST_MM,
                    Weather2Collection.PARAMS.PRECIPITATION_PROBABILITY_PCT,
                    Weather2Collection.PARAMS.SUNSHINE_DURATION_H,
                    Weather2Collection.PARAMS.WIND_DIRECTION_DEG,
                    Weather2Collection.PARAMS.WEATHER_DESCRIPTOR_IMAGE,
                    Weather2Collection.PARAMS.WIND_SPEED_MS_AVG,
                    Weather2Collection.PARAMS.AIR_TEMP_C_AVG,
                ].join(',')
            })
        });

        weather
            .update()
            .done(() => {

                this.set(weather.models);
                fetched.resolve(this);
            })
            .fail(() => {

                fetched.reject();
            });

        return fetched;
    }

    /**
    * @Description Fetch hourly weather data for 24 hours for given number of days
    *
    * @param {Moment} [fromDate] - From date to fetch weather
    * @param {Moment} [tillDate] - Till date to fetch weather
    *
    * @returns {object} $.Deferred()
    *
    * @memberOf Weather2Collection
    */
    fetchHourlyWeatherPrediction({ fromDate, tillDate, fieldUuid }) {

        let fetched = $.Deferred();
        let weather = new Weather2Collection([], {
            filterParams: new BaseFilterModel({
                foreignUuid: [fieldUuid],
                $fromDate: BaseModel.serializeDate(fromDate),
                $tillDate: BaseModel.serializeDate(tillDate),
                format: Weather2Collection.FORMAT.HOURLY,
                $params: [
                    Weather2Collection.PARAMS.START_DATETIME,
                    Weather2Collection.PARAMS.AIR_TEMP_C_AVG,
                    Weather2Collection.PARAMS.PRECIPITATION_BEST_MM,
                    Weather2Collection.PARAMS.CLOUD_COVER_PCT_AVG,
                    Weather2Collection.PARAMS.PRECIPITATION_PROBABILITY_PCT,
                    Weather2Collection.PARAMS.RELATIVE_HUMIDITY_PCT_AVG,
                    Weather2Collection.PARAMS.WIND_SPEED_MS_AVG,
                    Weather2Collection.PARAMS.WIND_DIRECTION_DEG,
                    Weather2Collection.PARAMS.WEATHER_DESCRIPTOR_IMAGE,
                    Weather2Collection.PARAMS.BELOW_DEVICE_TEMP_C_AVG,
                    Weather2Collection.PARAMS.LEAF_WETNESS_DURATION_H,
                    Weather2Collection.PARAMS.WATER_BALANCE_MM,
                    Weather2Collection.PARAMS.DELTA_TC_AVG
                ].join(',')
            })
        });

        weather.update()
            .done(() => {

                const toBeFetchedItems = tillDate.diff(fromDate, 'hours') + 1;

                if (toBeFetchedItems > weather.length) {

                    for (let h = 0; h < toBeFetchedItems; h++) {

                        const matchedItem = weather.find((item) => {

                            let time = item.get('startDatetime');
                            return time.isSame(fromDate.clone().add(h, 'hours'), 'hour');
                        });

                        if (!matchedItem) {
                            
                            weather.add({
                                startDatetime: fromDate.clone().add(h, 'hours')
                            });
                        }
                    }
                }

                this.set(weather.models);
                fetched.resolve(this);
            })
            .fail(() => { fetched.reject(); });

        return fetched;
    }

    /**
    * @Description Fetch hourly spray weather data for the given date range
    *
    * @param {Moment} [fromDate] - From date to fetch weather
    * @param {Moment} [tillDate] - Till date to fetch weather
    *
    * @returns {object} $.Deferred()
    *
    * @memberOf Weather2Collection
    */
    fetchHourlySprayWeatherPrediction({ fromDate, tillDate, fieldUuid }) {

        let fetched = $.Deferred();
        let weather = new SprayWeatherCollection([], {
            filterParams: new BaseFilterModel({
                fromDate: BaseModel.serializeDate(fromDate),
                toDate: BaseModel.serializeDate(tillDate),
                fieldUuid: [fieldUuid],
            })
        });

        weather.update()
            .done(() => {
                this.set(weather.models);
                fetched.resolve(this);
            })
            .fail(() => { fetched.reject(); });

        return fetched;
    }
}


// WEBPACK FOOTER //
// ./shared/collection/weather/Weather2Collection.js